import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV, ROLE_STAGE_COLORS } from './nav';

const STAGES = ['open', 'screening', 'interviewing', 'offer', 'filled', 'closed'];

const Recruitment: React.FC = () => {
    const { api } = useInternalApi();
    const [roles, setRoles] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [departments, setDepartments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ role_title: '', department: 'support', headcount: 1, target_start: '', notes: '' });

    useEffect(() => { document.title = 'Recruitment - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('hr-api/recruitment/');
            if (r.data?.status === 'success') {
                setRoles(r.data.data.roles || []);
                setSummary(r.data.data.summary);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('hr-api/reference/').then(r => { if (r.data?.status === 'success') setDepartments(r.data.data.departments || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (!form.role_title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Title required', text: 'Give the role a title.' });
            return;
        }
        try {
            await api.post('hr-api/recruitment/', form);
            setShowAdd(false);
            setForm({ role_title: '', department: 'support', headcount: 1, target_start: '', notes: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not open role.' });
        }
    };

    const patch = async (r: any, payload: any) => {
        try { await api.patch(`hr-api/recruitment/${r.id}/`, payload); load(); }
        catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update role.' }); }
    };

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUserPlus} className="me-2 text-primary" />Recruitment & Onboarding</h2>
                    <p className="text-muted mb-0">Open roles and where each one has reached</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Open Role</button>
            </div>

            {loading || !summary ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Open roles</div><h4>{summary.open_roles}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Open headcount</div><h4>{summary.open_headcount}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Candidates in process</div><h4>{summary.candidates_in_process}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Filled all time</div><h4 className="text-success">{summary.filled_all_time}</h4>
                        </div></div></div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {roles.length === 0 ? (
                                <div className="text-center py-5 text-muted">No roles open.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Role</th><th>Department</th><th>Headcount</th><th>Candidates</th><th>Days open</th><th>Stage</th></tr></thead>
                                    <tbody>
                                        {roles.map(r => (
                                            <tr key={r.id}>
                                                <td><strong>{r.role_title}</strong>{r.notes && <div className="small text-muted">{r.notes}</div>}</td>
                                                <td className="small">{r.department}</td>
                                                <td>{r.headcount}</td>
                                                <td style={{ maxWidth: 110 }}>
                                                    <input type="number" min={0} className="form-control form-control-sm" defaultValue={r.candidates_in_process}
                                                        onBlur={(e) => Number(e.target.value) !== r.candidates_in_process && patch(r, { candidates_in_process: Number(e.target.value) })} />
                                                </td>
                                                <td>{r.days_open}</td>
                                                <td style={{ minWidth: 150 }}>
                                                    <select className="form-select form-select-sm" value={r.stage} onChange={(e) => patch(r, { stage: e.target.value })}>
                                                        {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Open a Role</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Role title</label>
                                    <input className="form-control" value={form.role_title} onChange={(e) => setForm(f => ({ ...f, role_title: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Department</label>
                                        <select className="form-select" value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))}>
                                            {departments.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-3">
                                        <label className="form-label">Headcount</label>
                                        <input type="number" min={1} className="form-control" value={form.headcount}
                                            onChange={(e) => setForm(f => ({ ...f, headcount: Number(e.target.value) }))} />
                                    </div>
                                    <div className="col-3">
                                        <label className="form-label">Target start</label>
                                        <input type="date" className="form-control" value={form.target_start}
                                            onChange={(e) => setForm(f => ({ ...f, target_start: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Open Role</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Recruitment;
